import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { DialogService, GameService, InternationalizationService, SessionService, User } from '@app/core/services';
import { DialogConfig, GameErrorComponent } from '@app/shared/game-error/game-error.component';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { JeopardyGame, JeopardyUser } from '@app/shared/graphql/types';

type ViewRoom = JeopardyGame & { host: JeopardyUser, currentPlayers: number, alreadyJoined: boolean, missingPlayers: number };

@Component({
    selector: 'jeopardy-rooms',
    templateUrl: './rooms.component.html',
    styleUrls: ['./rooms.component.scss'],
    imports: [FormsModule, RouterLink, AsyncPipe]
})
export class RoomsComponent implements OnInit {
  public rooms: ViewRoom[];
  public filteredRooms$: Observable<ViewRoom[]>;
  public currentUser: User;
  public roomInput: string;
  public filterQueryChanged: Subject<string>;
  public reloadRooms: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public loadRoomsSub: Subscription;
  public gameInProgress: number;
  

  private router = inject(Router);
  public i18n = inject(InternationalizationService);
  public gameService = inject(GameService);
  public sessionService = inject(SessionService);
  public dialogService = inject(DialogService);

  ngOnInit(): void {
    this.rooms = [];
    this.filterQueryChanged = new Subject<string>();
    this.roomInput = '';
    this.currentUser = this.sessionService.getUser();
    this.gameInProgress = +this.gameService.getGameId() || null;

    this.loadRoomsSub = this.reloadRooms.pipe(
      switchMap(() => this.gameService.getRoomsList('')),
      map(rooms => rooms.map((room) => {
        const currentPlayers = room.jeopardyUsers.filter(user => user.name).length;
        return {
          ...room,
          host: room.jeopardyUsers.find(user => user.host),
          currentPlayers,
          alreadyJoined: room.jeopardyUsers.filter(user => user.name === this.currentUser.username).length > 0,
          missingPlayers: room.players - currentPlayers,
        } as ViewRoom;
      }))
    ).subscribe({
      next: (rooms) => {
        this.rooms = rooms;
        this.filterQueryChanged.next(this.roomInput || '');

        if(this.gameInProgress) {
          const activeRoom = this.rooms.filter(room => room.gameId === this.gameInProgress);

          if(activeRoom.length === 0) {
            this.gameService.setGameId(null);
            this.sessionService.updateUserGameId(null);
            this.gameInProgress = null;
          }
        }
      },
      complete: () => console.log('loadRoomsSub Complete'),
      error: (error) => console.log(error)
    });

    this.filteredRooms$ = this.filterQueryChanged.pipe(
      map(query => {
        if(query) {
          const filtered = this.rooms.filter(room => room.roomName
            .toLowerCase().indexOf(query.toLowerCase()) !== -1);

          console.log(filtered);

          return filtered;
        };

        return this.rooms;
      })
    );
  }

  public joinRoom(event: MouseEvent, room: JeopardyGame) {
    event.preventDefault();
    let params = {
      accessCode: '',
      lang: this.currentUser.location.language.isoCode2Letters,
      gameId: room.gameId
    };

    if(room.private) {
      this.router.navigate(['/multiplayer/join-private', room.gameId]);
      return;
    }

    this.gameService.joinRoom(params.accessCode, params.lang, params.gameId).subscribe(data => {
      if(data.errors) {
        console.error('joinRoom - errors', data.errors);
        return;
      }

      if(data.entity) {
        const currentDevice = this.gameService.getDeviceDetails();

        this.gameService.getUserById(data.entity.userId).subscribe(user => {
          console.log(user);
          
          const userUpdate = {
            ...user,
            name: this.currentUser.username,
            connected: true,
            language: this.currentUser.location.language.isoCode2Letters,
            location: this.currentUser.location.country.countryId,
            deviceDetected: currentDevice[0],
            deviceIdentifier: currentDevice[1],
            avatar: this.currentUser.avatar.split('/').pop(),
            host: false,
            jokerFiftyFifty: user.jokerFiftyFifty || 0,
            jokerSkip: user.jokerFiftyFifty || 0
          };

          this.gameService.updateUser(userUpdate).subscribe(_ => {
            this.gameService.setGameId(room.gameId);
            this.sessionService.updateUserGameId(room.gameId);
            this.router.navigate(['/multiplayer/lobby', room.gameId]);
          });
        });
      }
    });
  }

  public joinLobby(event: MouseEvent, room: JeopardyGame) {
    event.preventDefault();
    this.router.navigate(['/multiplayer/lobby', room.gameId]);
  }

  public searchRooms() {
    if(!this.roomInput) {
      this.filterQueryChanged.next('');
      return;
    }

    this.filterQueryChanged.next(this.roomInput);
  }

  public createRoom(ev: Event) {
    ev.preventDefault();

    if(this.gameInProgress) {
      const config: DialogConfig = {
        header: this.i18n.str('challengeu.dialog_warning_title', 'Warning')(),
        message: this.i18n.str('challengeu.dialog_game_in_progress',"You already have a game in progress.")(),
        showAcceptButton: false,
        cancelButtonLabel: this.i18n.str('challengeu.dialog_close','CLOSE')(),
      };

      this.dialogService.open(GameErrorComponent, config);

      return;
    }
    
    this.router.navigate(['/multiplayer/create-room']);
  }

  public refreshRooms(event: MouseEvent) {
    event.preventDefault();
    this.reloadRooms.next(true);
  }

  ngOnDestry() {
    this.loadRoomsSub.unsubscribe();
  }
}
